import { useState, useEffect } from 'react';
import AssignmentGroupMembers from './AssignmentGroupMembers';
import {
  fetchCoursePlacementLabels,
  getCourseGroupDescription,
  getCourseGroupDisplayName
} from '../../../utils/groupLabels';
import {
  getEffectiveNow,
  getEffectiveToday,
  isOpenEverything
} from '../../../utils/systemTime';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders() {
  return { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY };
}

async function fetchWeeks(courseId) {
  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&cq_enabled=eq.true&select=*&order=week_number.asc`,
    { headers: apiHeaders() }
  );
  return r.json();
}

async function fetchEnrolment(studentId, courseId) {
  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${studentId}&course_id=eq.${courseId}&select=*&limit=1`,
    { headers: apiHeaders() }
  );
  const data = await r.json();
  return data[0] || null;
}

async function fetchAssignedQuestions(weekId, stream, groupNumber) {
  // Get question assignments for this group
  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_question_assignments?course_week_id=eq.${weekId}&stream=eq.${stream}&group_number=eq.${groupNumber}&select=*,cq_submissions(id,question_text)`,
    { headers: apiHeaders() }
  );
  return r.json();
}

async function fetchMySubmissions(enrolmentId, assignmentIds) {
  if (!assignmentIds) return [];
  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_question_improvements?enrolment_id=eq.${enrolmentId}&cq_question_assignment_id=in.(${assignmentIds})&select=*`,
    { headers: apiHeaders() }
  );
  return r.json();
}

async function fetchCourseEnrolments(courseId) {
  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=id,stream,group_number,students(student_code,english_name,chinese_name,is_master)`,
    { headers: apiHeaders() }
  );

  const rows = await r.json();

  return (Array.isArray(rows) ? rows : [])
    .filter(
      enrolment =>
        enrolment?.students?.is_master !== true
    )
    .sort((a, b) =>
      String(a?.students?.student_code || '')
        .localeCompare(
          String(b?.students?.student_code || ''),
          undefined,
          {
            numeric: true,
            sensitivity: 'base'
          }
        )
    );
}

async function fetchQiExtensionsForGroup(
  weekIds,
  stream,
  groupNumber
) {
  if (
    !weekIds ||
    stream === null ||
    stream === undefined ||
    groupNumber === null ||
    groupNumber === undefined
  ) {
    return [];
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/qi_group_extensions?course_week_id=in.(${weekIds})&stream=eq.${encodeURIComponent(stream)}&group_number=eq.${encodeURIComponent(groupNumber)}&select=*`,
      {
        headers:
          apiHeaders()
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Loading QI extensions failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  return response.json();
}


async function saveImprovement(data) {
  const id =
    data?.id || null;

  const payload = {
    ...data
  };

  delete payload.id;

  const response = await fetch(
    id
      ? SUPABASE_URL +
        '/rest/v1/cq_question_improvements?id=eq.' +
        encodeURIComponent(id)
      : SUPABASE_URL +
        '/rest/v1/cq_question_improvements',
    {
      method:
        id
          ? 'PATCH'
          : 'POST',

      headers: {
        ...apiHeaders(),
        'Content-Type':
          'application/json',
        'Prefer':
          'return=representation'
      },

      body:
        JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Saving QI failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  const saved =
    Array.isArray(rows)
      ? rows[0]
      : null;

  if (!saved) {
    throw new Error(
      'QI save returned no record.'
    );
  }

  return saved;
}

function getWindowStatus(
  week,
  course,
  session,
  extensionMinutes = 0
) {
  if (
    isOpenEverything(
      session
    )
  ) {
    return 'open';
  }

  if (!week?.class_date) {
    return 'not_open';
  }

  const now =
    getEffectiveNow(
      session
    );

  const classDate =
    new Date(
      `${week.class_date}T00:00:00`
    );

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const day =
    dayNames[
      classDate.getDay()
    ];

  const classTimeStr =
    (
      course.class_times &&
      course.class_times[day]
    ) ||
    course.class_time ||
    '09:00';

  const [
    hours,
    minutes
  ] =
    classTimeStr
      .split(':')
      .map(Number);

  const openTime =
    new Date(
      classDate
    );

  openTime.setHours(
    hours,
    minutes,
    0,
    0
  );

  const closeTime =
    new Date(
      openTime
    );

  closeTime.setHours(
    closeTime.getHours() +
      4
  );

  closeTime.setMinutes(
    closeTime.getMinutes() +
      Math.max(
        0,
        Number(
          extensionMinutes
        ) || 0
      )
  );

  if (now < openTime) {
    return 'not_open';
  }

  if (now > closeTime) {
    return 'closed';
  }

  return 'open';
}


const QUESTION_PROBLEM_TYPES = [
  {
    title: "Opinion Polling",
    description: "Questions that merely ask what people think about a topic are not appropriate for a philosophical discussion. Instead of asking us to consider philosophical arguments and concepts, they shift the focus to what people happen to think. But what people happen to think has no bearing on whether that way of thinking is correct or incorrect. At best, such questions invite speculation; at worst, they substitute sociology for philosophy.",
    unproductive: [
      "“What do most people think about capital punishment?”",
      "“Why are people uncomfortable with AI making decisions?”",
    ],
    revised: [
      "“What moral reasons are commonly given in support of capital punishment, and how strong are they?”",
      "“What ethical concerns arise when AI systems make decisions that affect people’s lives, and how should those concerns be evaluated?”",
    ]
  },
  {
    title: "Empirical Questions",
    description: "Some questions ask whether one thing causes another, or whether a policy has certain effects. Answering such questions responsibly would require empirical evidence, data, or specialist knowledge that we do not have in class. Discussion then risks becoming speculative, anecdotal, or driven by unsupported assumptions. Philosophical discussion cannot focus on settling empirical facts that we cannot establish or make progress on through discussion.",
    unproductive: [
      "“Do longer prison sentences actually reduce crime?”",
      "“Does social media make people more politically extreme?”",
    ],
    revised: [
      "“If longer prison sentences were shown not to reduce crime, what moral implications would that have for punishment policy?”",
      "“Why would political polarisation, if caused by social media, be morally troubling, and how much weight should that concern carry?”",
    ]
  },
  {
    title: "Category Confusion",
    description: "Some questions blur together distinct kinds of issues, making it unclear what is actually being asked. Common confusions include moral obligation versus legal right, justice versus charity, individual duties versus state policies, or ideal principles versus real-world implementation. When categories are mixed together, we talk past each other and cannot make meaningful progress.",
    unproductive: [
      "“Do states have the right to control their borders?”",
      "“Is free speech justified?”",
    ],
    revised: [
      "“Do states have a moral right, rather than merely a legal power, to control their borders?”",
      "“Is free speech morally justified, as opposed to legally protected or socially valuable?”",
    ]
  },
  {
    title: "Smuggled Conclusions",
    description: "Some questions are framed in a way that already assumes a moral answer. Loaded terms, emotionally charged language, or one-sided descriptions can settle the issue before discussion begins. A good philosophical question should leave the moral conclusion open, even when the topic is emotionally or politically charged.",
    unproductive: [
      "“Is it wrong to punish people for actions they couldn’t control?”",
      "“Why is exploiting workers morally unacceptable?”",
    ],
    revised: [
      "“To what extent does lack of control undermine moral responsibility for an action?”",
      "“Under what conditions, if any, does unequal bargaining power make an employment relationship morally problematic?”",
    ]
  },
  {
    title: "Scale Confusion",
    description: "Some questions fail to specify the level at which the issue is being considered. They move ambiguously between individuals, states, and the global system. When the level of analysis is unclear, participants may talk past one another while appearing to disagree.",
    unproductive: [
      "“Do we have a duty to address climate change?”",
      "“Is it wrong to lie?”",
    ],
    revised: [
      "“What duties, if any, do individual persons have to address climate change, as opposed to states or corporations?”",
      "“Is lying morally wrong when done by individuals, or does its wrongness primarily arise in institutional settings such as law or testimony?”",
    ]
  },
  {
    title: "Practical Feasibility",
    description: "Some questions focus on whether a proposal is realistic or feasible rather than on whether it is morally justified. This can make it unclear whether feasibility is being treated as a moral consideration or simply assumed as a constraint. Good discussion questions clarify the role feasibility is meant to play, rather than letting it substitute for moral argument.",
    unproductive: [
      "“Is it realistic to expect people to act altruistically?”",
      "“Is universal basic income realistic?”",
    ],
    revised: [
      "“What role should feasibility play in assessing moral demands on individual behaviour?”",
      "“If a policy like universal basic income were morally justified but difficult to implement, what follows from that?”",
    ]
  },
  {
    title: "False Dilemmas",
    description: "Some questions assume that only two positions are available, when the issue actually allows for mixed, conditional, or threshold views. By forcing an artificial choice, such questions narrow the discussion and obscure more nuanced positions that may be philosophically interesting.",
    unproductive: [
      "“Is punishment about revenge or deterrence?”",
      "“Are moral actions motivated by reason or emotion?”",
    ],
    revised: [
      "“What different aims might punishment serve, and how should conflicts between those aims be resolved?”",
      "“How might reason and emotion play a role in moral motivation?”",
    ]
  },
  {
    title: "Unmotivated Definitions",
    description: "Questions that stop at asking what a concept means often fail to generate sustained philosophical discussion. While definitions matter, a question that only asks for a definition does not yet indicate what is at stake or why different interpretations would matter. Strong discussion questions move beyond definition by testing a concept, contrasting interpretations, or asking what follows from understanding it in a particular way.",
    unproductive: [
      "“What is justice?”",
      "“What does autonomy mean?”",
    ],
    revised: [
      "“How do different conceptions of justice lead to different evaluations of punishment?”",
      "“If autonomy requires the ability to reflect on one’s desires, what implications does that have for assessing consent?”",
    ]
  },
  {
    title: "Opinion-Mongering",
    description: "Some questions are framed in a way that primarily invites participants to state whether they agree or disagree. While such questions can provoke discussion in a good class, they place little pressure on participants to articulate reasons or engage with competing views. A stronger question shifts the focus from personal agreement to the structure and evaluation of arguments.",
    unproductive: [
      "“Do you agree that lying is sometimes acceptable?”",
      "“Should we be worried about artificial intelligence?”",
    ],
    revised: [
      "“Under what conditions, if any, can lying be morally justified, and what principles would support that judgment?”",
      "“What kinds of risks associated with artificial intelligence raise genuine moral concerns, and why?”",
    ]
  },
  {
    title: "Unmotivated Hypotheticals",
    description: "Hypothetical cases are common in philosophy, but they do not automatically improve a discussion. When a hypothetical is introduced without a clear purpose, discussion can drift toward imaginative speculation rather than philosophical analysis. A well-framed question makes clear what feature of the case is being tested and how the hypothetical bears on a broader issue or principle.",
    unproductive: [
      "“Imagine a world where no one ever lied. Would that be better?”",
      "“Suppose humans could live forever. Would that be good?”",
    ],
    revised: [
      "“If lying were impossible, what moral functions currently served by truthfulness would be lost or preserved?”",
      "“If human lives were indefinitely long, which moral values tied to finitude would be challenged, if any?”",
    ]
  },
  {
    title: "Unstructured Disagreement",
    description: "Some questions invite disagreement but do not indicate where or why disagreement might arise. As a result, discussion can generate opposing answers without clarifying the underlying reasons for disagreement. Strong philosophical questions anticipate points of reasonable disagreement and frame the issue so that competing considerations can be examined rather than merely asserted. A good question places pressure on a concept by asking where it might fail.",
    unproductive: [
      "“Is technology making society better or worse?”",
      "“Is moral responsibility real?”",
    ],
    revised: [
      "“When a technology brings significant benefits but also predictable harms, what should determine whether its use is morally acceptable?”",
      "“When an agent’s actions are heavily shaped by factors beyond their control, to what extent should they still be held morally responsible?”",
    ]
  },
];;

function ProblemAccordion({ item }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={problemGuideStyles.accordion}>
      <button
        type="button"
        style={problemGuideStyles.accordionHeader}
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
      >
        <span>{item.title}</span>
        <span style={problemGuideStyles.accordionIcon}>
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <div style={problemGuideStyles.accordionContent}>
          <p style={problemGuideStyles.description}>
            {item.description}
          </p>

          <div style={problemGuideStyles.listHeading}>
            Unproductive Questions
          </div>
          <ul style={problemGuideStyles.list}>
            {item.unproductive.map(question => (
              <li
                key={`${item.title}-unproductive-${question}`}
                style={problemGuideStyles.listItem}
              >
                {question}
              </li>
            ))}
          </ul>

          <div style={problemGuideStyles.listHeading}>
            Revised Questions
          </div>
          <ul style={problemGuideStyles.list}>
            {item.revised.map(question => (
              <li
                key={`${item.title}-revised-${question}`}
                style={problemGuideStyles.listItem}
              >
                {question}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ProblemGuideModal({ onClose }) {
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      style={problemGuideStyles.overlay}
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        style={problemGuideStyles.modal}
        onMouseDown={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qi-problem-guide-title"
      >
        <div style={problemGuideStyles.header}>
          <h3
            id="qi-problem-guide-title"
            style={problemGuideStyles.title}
          >
            Common Problems with Discussion Questions
          </h3>
          <button
            type="button"
            style={problemGuideStyles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={problemGuideStyles.body}>
          {QUESTION_PROBLEM_TYPES.map(item => (
            <ProblemAccordion
              key={item.title}
              item={item}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const problemGuideStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    background: 'rgba(31, 41, 51, 0.48)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  modal: {
    width: '100%',
    maxWidth: '850px',
    maxHeight: '86vh',
    background: 'white',
    borderRadius: '6px',
    boxShadow: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '16px 18px',
    borderBottom: '1px solid #d9e2ec',
    flexShrink: 0
  },
  title: {
    margin: 0,
    fontSize: '18px',
    color: '#243b53'
  },
  closeBtn: {
    width: '34px',
    height: '34px',
    border: 'none',
    borderRadius: '5px',
    background: '#edf1f5',
    color: '#243b53',
    fontSize: '24px',
    lineHeight: 1,
    cursor: 'pointer',
    flexShrink: 0
  },
  body: {
    padding: '16px 18px 18px',
    overflowY: 'auto'
  },
  accordion: {
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    background: '#fff',
    marginBottom: '12px',
    overflow: 'hidden'
  },
  accordionHeader: {
    width: '100%',
    border: 'none',
    padding: '14px 18px',
    fontWeight: '700',
    fontSize: '14px',
    color: '#243b53',
    background: '#f4f7f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    cursor: 'pointer',
    textAlign: 'left'
  },
  accordionIcon: {
    fontSize: '20px',
    lineHeight: 1,
    fontWeight: '400',
    color: '#627d98',
    flexShrink: 0
  },
  accordionContent: {
    padding: '16px 18px 18px',
    borderTop: '1px solid #e3e9ee',
    color: '#243b53',
    fontSize: '14px',
    lineHeight: '1.6'
  },
  description: {
    margin: '0 0 14px'
  },
  listHeading: {
    fontWeight: '700',
    margin: '12px 0 6px'
  },
  list: {
    margin: '0 0 12px',
    paddingLeft: '22px'
  },
  listItem: {
    marginBottom: '5px'
  }
};


// ─── Question Form ────────────────────────────────────────────────────────────

function QuestionForm({ assignment, existingSubmission, enrolmentId, onSaved, disabled }) {
  const [showProblemGuide, setShowProblemGuide] = useState(false);
  const originalText = assignment.cq_submissions?.question_text || '';
  const [problems, setProblems] = useState(existingSubmission?.problems_identified || '');
  const [revised, setRevised] = useState(existingSubmission?.revised_question || '');
  const [fix, setFix] = useState(existingSubmission?.fix_explanation || '');
  const [saving, setSaving] = useState(false);

  async function handleSave(submit = false) {
    setSaving(true);
    try {
      const data = {
        ...(existingSubmission?.id ? { id: existingSubmission.id } : {}),
        cq_question_assignment_id: assignment.id,
        enrolment_id: enrolmentId,
        group_member_ids: existingSubmission?.group_member_ids || [],
        problems_identified: problems,
        revised_question: revised,
        fix_explanation: fix,
        submitted: submit,
        submitted_at: submit ? new Date().toISOString() : null
      };
      const result = await saveImprovement(data);
      onSaved(result, submit);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={qfStyles.wrap}>
      {showProblemGuide && (
        <ProblemGuideModal
          onClose={() => setShowProblemGuide(false)}
        />
      )}

      <div style={qfStyles.originalBox}>
        <div style={qfStyles.fieldLabel}>Original question</div>
        <div style={qfStyles.originalText}>{originalText}</div>
      </div>

      <div style={qfStyles.field}>
        <label style={qfStyles.fieldLabel}>What problems does it have?</label>
        <button
          type="button"
          style={qfStyles.problemGuideLink}
          onClick={() => setShowProblemGuide(true)}
        >
          (See examples of kinds of problems here)
        </button>
        <textarea
          style={qfStyles.textarea}
          value={problems}
          onChange={e => setProblems(e.target.value)}
          disabled={disabled}
          placeholder="Describe the problems with this question..."
          rows={3}
        />
      </div>

      <div style={qfStyles.field}>
        <label style={qfStyles.fieldLabel}>Revised question</label>
        <textarea
          style={qfStyles.textarea}
          value={revised}
          onChange={e => setRevised(e.target.value)}
          disabled={disabled}
          placeholder="Write your improved version..."
          rows={3}
        />
      </div>

      <div style={qfStyles.field}>
        <label style={qfStyles.fieldLabel}>How does the revision fix the problems?</label>
        <textarea
          style={qfStyles.textarea}
          value={fix}
          onChange={e => setFix(e.target.value)}
          disabled={disabled}
          placeholder="Explain how your revision addresses the problems..."
          rows={3}
        />
      </div>

      {!disabled && (
        <div style={qfStyles.actions}>
          <button style={qfStyles.draftBtn} disabled={saving} onClick={() => handleSave(false)}>
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      )}
    </div>
  );
}

const qfStyles = {
  wrap: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '16px',
    background: 'white',
    marginBottom: '16px'
  },

  originalBox: {
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    background: '#f7f8fa',
    borderRadius: '5px',
    padding: '12px',
    marginBottom: '14px',
    border: '1px solid #d9e2ec'
  },

  field: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    marginBottom: '14px'
  },
  fieldLabel: { display: 'block', fontWeight: '700', fontSize: '13px', color: '#243b53', marginBottom: '6px' },
  problemGuideLink: {
    display: 'block',
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: '-1px 0 8px',
    color: '#2f6f9f',
    fontSize: '13px',
    fontFamily: 'inherit',
    textDecoration: 'underline',
    cursor: 'pointer',
    textAlign: 'left'
  },
  originalText: {
    fontSize: '15px',
    color: '#243b53',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word'
  },

  textarea: {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    border: '1px solid #d9e2ec',
    borderRadius: '5px',
    padding: '9px 11px',
    fontSize: '14px',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    overflowX: 'hidden'
  },
  actions: { display: 'flex', gap: '8px' },
  draftBtn: { background: '#edf1f5', color: '#243b53', border: '1px solid #d9e2ec', borderRadius: '5px', padding: '8px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function StudentQi({ session, course, enrolment, onBack }) {
  const [weeks, setWeeks] = useState([]);
  const [myEnrolment, setMyEnrolment] = useState(enrolment || null);
  const [courseEnrolments, setCourseEnrolments] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [qiExtensions, setQiExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coursePlacementLabels, setCoursePlacementLabels] =
    useState({ streams: {}, groups: {} });

  // Group member IDs entry
  const [memberCodes, setMemberCodes] = useState('');
  const [memberError, setMemberError] = useState('');
  const [resolvedMembers, setResolvedMembers] = useState(null); // [{id, name}]

  // Which questions selected for submission
  const [selectedQ1, setSelectedQ1] = useState('');
  const [selectedQ2, setSelectedQ2] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    fetchCoursePlacementLabels(course.id)
      .then(labels => {
        if (!cancelled) {
          setCoursePlacementLabels(
            labels || { streams: {}, groups: {} }
          );
        }
      })
      .catch(err => {
        console.error(
          'Loading Course Group name failed:',
          err
        );
      });

    return () => {
      cancelled = true;
    };
  }, [course.id]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const [w, ce, freshEnrolment] = await Promise.all([
          fetchWeeks(course.id),
          fetchCourseEnrolments(course.id),
          fetchEnrolment(session.studentId, course.id)
        ]);

        if (cancelled) return;

        const loadedWeeks = Array.isArray(w) ? w : [];

        setWeeks(loadedWeeks);
        setCourseEnrolments(Array.isArray(ce) ? ce : []);

        // Do not trust the enrolment object passed by the parent here:
        // it may have been loaded before the instructor assigned the
        // student's stream/group. Always use the current Supabase row.
        setMyEnrolment(freshEnrolment || null);

        const today = getEffectiveToday(session);
        const pastWeeks = loadedWeeks.filter(
          wk => wk.class_date <= today
        );

        const defaultWeek =
          pastWeeks.length > 0
            ? pastWeeks[pastWeeks.length - 1]
            : loadedWeeks[0];

        setSelectedWeekId(
          defaultWeek ? defaultWeek.id : null
        );
      } catch (err) {
        if (!cancelled) {
          console.error(
            'Failed to load Question Improvement:',
            err
          );
          setError(
            'Failed to load Question Improvement.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id, session.studentId]);

  useEffect(() => {
    if (!course?.id || !session?.studentId) {
      return undefined;
    }

    // If this page is open while the instructor assigns the student,
    // keep refreshing only until stream/group values appear.
    if (
      myEnrolment?.stream &&
      myEnrolment?.group_number
    ) {
      return undefined;
    }

    let cancelled = false;

    async function refreshEnrolment() {
      try {
        const fresh = await fetchEnrolment(
          session.studentId,
          course.id
        );

        if (!cancelled && fresh) {
          setMyEnrolment(fresh);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(
            'Failed to refresh QI enrolment:',
            err
          );
        }
      }
    }

    const intervalId = window.setInterval(
      refreshEnrolment,
      5000
    );

    const handleFocus = () => {
      refreshEnrolment();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshEnrolment();
      }
    };

    window.addEventListener(
      'focus',
      handleFocus
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    return () => {
      cancelled = true;

      window.clearInterval(intervalId);

      window.removeEventListener(
        'focus',
        handleFocus
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );
    };
  }, [
    course?.id,
    session?.studentId,
    myEnrolment?.stream,
    myEnrolment?.group_number
  ]);

  useEffect(() => {
    if (
      !myEnrolment?.stream ||
      !myEnrolment?.group_number ||
      weeks.length === 0
    ) {
      setQiExtensions(
        []
      );

      return;
    }

    let cancelled =
      false;

    const weekIds =
      weeks
        .map(
          week =>
            week.id
        )
        .join(',');

    fetchQiExtensionsForGroup(
      weekIds,
      myEnrolment.stream,
      myEnrolment.group_number
    )
      .then(
        rows => {
          if (!cancelled) {
            setQiExtensions(
              Array.isArray(
                rows
              )
                ? rows
                : []
            );
          }
        }
      )
      .catch(
        err => {
          if (!cancelled) {
            console.error(
              'Failed to load QI extensions:',
              err
            );

            setQiExtensions(
              []
            );
          }
        }
      );

    return () => {
      cancelled =
        true;
    };
  }, [
    weeks,
    myEnrolment?.stream,
    myEnrolment?.group_number
  ]);


  useEffect(() => {
    if (!selectedWeekId || !myEnrolment) return;

    async function loadWeekData() {
      if (
        !myEnrolment?.stream ||
        !myEnrolment?.group_number
      ) {
        setAssignments([]);
        setMySubmissions([]);
        setSubmitted(false);
        setSelectedQ1('');
        setSelectedQ2('');
        return;
      }

      const week = weeks.find(
        w =>
          String(w.id) ===
          String(selectedWeekId)
      );

      if (!week) {
        return;
      }

      const qa =
        await fetchAssignedQuestions(
          selectedWeekId,
          myEnrolment.stream,
          myEnrolment.group_number
        );

      const safeAssignments =
        Array.isArray(qa)
          ? qa
          : [];

      setAssignments(
        safeAssignments
      );

      if (
        safeAssignments.length === 0
      ) {
        setMySubmissions([]);
        setSubmitted(false);
        setSelectedQ1('');
        setSelectedQ2('');
        return;
      }

      const subs =
        await fetchMySubmissions(
          myEnrolment.id,
          safeAssignments
            .map(a => a.id)
            .join(',')
        );

      const safeSubs =
        Array.isArray(subs)
          ? subs
          : [];

      setMySubmissions(
        safeSubs
      );

      const requiredCount =
        safeAssignments.length >= 2
          ? 2
          : 1;

      const submittedAssignmentIds =
        new Set(
          safeSubs
            .filter(
              submission =>
                submission.submitted ===
                true
            )
            .map(
              submission =>
                String(
                  submission
                    .cq_question_assignment_id
                )
            )
        );

      setSubmitted(
        submittedAssignmentIds.size >=
          requiredCount
      );

      setSelectedQ1(
        safeSubs[0]
          ?.cq_question_assignment_id ||
        ''
      );

      setSelectedQ2(
        requiredCount === 2
          ? (
              safeSubs[1]
                ?.cq_question_assignment_id ||
              ''
            )
          : ''
      );
    }

    loadWeekData();
  }, [
    selectedWeekId,
    myEnrolment,
    weeks,
    course,
    session
  ]);

  async function handleResolveMember() {
    setMemberError('');
    const codes = memberCodes.split(/[\s,]+/).map(c => c.trim()).filter(Boolean);
    if (!codes.length) {
      setResolvedMembers([]);
      return;
    }

    // Validate each code is enrolled in this course
    const found = [];
    const notFound = [];
    for (const code of codes) {
      const match = courseEnrolments.find(e => e.students?.student_code === code);
      if (match) {
        found.push({ id: match.id, name: match.students?.student_code, code });
      } else {
        notFound.push(code);
      }
    }

    if (notFound.length) {
      setMemberError(`Not enrolled in this course: ${notFound.join(', ')}`);
      return;
    }

    setResolvedMembers(found);
  }

  async function handleSubmit() {
    setError('');

    const requiredCount =
      assignments.length >= 2
        ? 2
        : 1;

    const selected =
      [selectedQ1, selectedQ2]
        .filter(Boolean)
        .slice(
          0,
          requiredCount
        );

    if (
      selected.length <
      requiredCount
    ) {
      setError(
        'You must complete ' +
          requiredCount +
          ' question' +
          (
            requiredCount > 1
              ? 's'
              : ''
          ) +
          ' before submitting.'
      );

      return;
    }

    const hasOtherCodes =
      memberCodes
        .trim()
        .length > 0;

    if (
      hasOtherCodes &&
      !resolvedMembers
    ) {
      setError(
        'Please validate the other group member IDs before submitting.'
      );

      return;
    }

    const memberIds =
      [
        ...new Set([
          String(
            myEnrolment.id
          ),

          ...(
            resolvedMembers ||
            []
          ).map(
            member =>
              String(member.id)
          )
        ])
      ];

    setSubmitting(true);

    try {
      for (
        const assignmentId of
          selected
      ) {
        const existing =
          mySubmissions.find(
            submission =>
              String(
                submission
                  .cq_question_assignment_id
              ) ===
              String(assignmentId)
          );

        if (!existing) {
          throw new Error(
            'Please save a draft for each question before submitting.'
          );
        }

        const saved =
          await saveImprovement({
            id:
              existing.id,

            cq_question_assignment_id:
              assignmentId,

            enrolment_id:
              myEnrolment.id,

            group_member_ids:
              memberIds,

            problems_identified:
              existing
                .problems_identified,

            revised_question:
              existing
                .revised_question,

            fix_explanation:
              existing
                .fix_explanation,

            submitted:
              true,

            submitted_at:
              new Date()
                .toISOString()
          });

        const returnedIds =
          new Set(
            [
              saved.enrolment_id,

              ...(
                Array.isArray(
                  saved
                    .group_member_ids
                )
                  ? saved
                      .group_member_ids
                  : []
              )
            ]
              .filter(Boolean)
              .map(
                id =>
                  String(id)
              )
          );

        const missing =
          memberIds.filter(
            id =>
              !returnedIds.has(
                String(id)
              )
          );

        if (
          saved.submitted !==
            true ||
          missing.length
        ) {
          throw new Error(
            'QI was not saved with every participating student ID.'
          );
        }
      }

      setSubmitted(true);

      setSuccess(
        'Question Improvement submitted successfully.'
      );

    } catch (err) {
      console.error(
        'QI submission failed:',
        err
      );

      setError(
        err?.message ||
        'Submission failed. Please try again.'
      );

    } finally {
      setSubmitting(false);
    }
}

  function handleDraftSaved(result, wasSubmit) {
    setMySubmissions(prev => {
      const exists = prev.find(s => s.id === result.id);
      if (exists) return prev.map(s => s.id === result.id ? result : s);
      return [...prev, result];
    });
    if (!wasSubmit) setSuccess('Draft saved.');
  }

  function getExtensionMinutesForWeek(
    weekId
  ) {
    const row =
      qiExtensions.find(
        extension =>
          String(
            extension
              .course_week_id
          ) ===
          String(
            weekId
          )
      );

    return Math.max(
      0,
      Number(
        row
          ?.extension_minutes
      ) || 0
    );
  }

  const selectedWeek =
    weeks.find(
      w =>
        w.id ===
        selectedWeekId
    );

  const selectedExtensionMinutes =
    getExtensionMinutesForWeek(
      selectedWeek?.id
    );

  const windowStatus =
    selectedWeek
      ? getWindowStatus(
          selectedWeek,
          course,
          session,
          selectedExtensionMinutes
        )
      : 'not_open';
  const isOpen = windowStatus === 'open';
  const isClosed = windowStatus === 'closed';
  const isReadOnly = submitted || isClosed;

  const quota = assignments.length >= 2 ? 2 : Math.min(assignments.length, 1);
  const availableForQ2 = assignments.filter(a => a.id !== selectedQ1);

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.muted}>
          Loading...
        </div>
      </div>
    );
  }

  if (!myEnrolment?.stream || !myEnrolment?.group_number) {
    return (
      <div style={styles.wrap}>
        <div style={styles.notice}>
          You have not been assigned to a stream and group yet. Please ask your instructor.
        </div>
      </div>
    );
  }

  const myQiGroup =
    myEnrolment?.group_number
      ? {
          groupNumber:
            myEnrolment.group_number,
          members: courseEnrolments
            .filter(row =>
              Number(row.stream) ===
                Number(myEnrolment.stream) &&
              Number(row.group_number) ===
                Number(myEnrolment.group_number)
            )
        }
      : null;

  return (
    <div style={styles.wrap}>
      <h2 style={styles.heading}>Question Improvement</h2>
      <p style={styles.muted}>
        Improve critical questions assigned to your group.
      </p>

      <AssignmentGroupMembers
        group={myQiGroup}
        title="Your QI Group"
        displayName={
          getCourseGroupDisplayName(
            coursePlacementLabels,
            myEnrolment?.stream,
            myEnrolment?.group_number
          )
        }
        description={
          getCourseGroupDescription(
            coursePlacementLabels,
            myEnrolment?.stream,
            myEnrolment?.group_number
          )
        }
      />

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Week selector */}
      <div style={styles.weekSelector}>
        {weeks.map(w => {
          const ws =
            getWindowStatus(
              w,
              course,
              session,
              getExtensionMinutesForWeek(
                w.id
              )
            );
          return (
            <button
              key={w.id}
              style={{
                ...styles.weekBtn,
                ...(w.id === selectedWeekId ? styles.weekBtnActive : {}),
                ...(ws === 'closed' ? styles.weekBtnClosed : {}),
                ...(ws === 'not_open' ? styles.weekBtnFuture : {})
              }}
              onClick={() => setSelectedWeekId(w.id)}
            >
              Week {w.week_number}
            </button>
          );
        })}
      </div>

      {selectedWeek && (
        <>
          {selectedExtensionMinutes > 0 && (
            <div
              style={
                styles.notice
              }
            >
              Your group has a Question Improvement extension of{' '}
              {Math.floor(
                selectedExtensionMinutes /
                60
              ) > 0
                ? Math.floor(
                    selectedExtensionMinutes /
                    60
                  ) +
                  ' hour' +
                  (
                    Math.floor(
                      selectedExtensionMinutes /
                      60
                    ) === 1
                      ? ''
                      : 's'
                  )
                : ''}
              {Math.floor(
                selectedExtensionMinutes /
                60
              ) > 0 &&
              selectedExtensionMinutes %
                60 >
                0
                ? ' '
                : ''}
              {selectedExtensionMinutes %
                60 >
                0
                ? (
                    selectedExtensionMinutes %
                    60
                  ) +
                  ' minute' +
                  (
                    selectedExtensionMinutes %
                      60 ===
                    1
                      ? ''
                      : 's'
                  )
                : ''}
              .
            </div>
          )}

          {windowStatus === 'not_open' && (
            <div style={styles.notice}>
              The Question Improvement window for this week has not opened yet. It opens at the start of class.
            </div>
          )}

          {windowStatus === 'closed' && !submitted && (
            <div style={styles.noticeWarning}>
              The submission window for this week has closed.
            </div>
          )}

          {submitted && (
            <div style={styles.successBox}>
              You have submitted your Question Improvement for this week.
            </div>
          )}

          {assignments.length === 0 && windowStatus !== 'not_open' && (
            <div style={styles.notice}>
              No questions have been assigned to your group for this week yet.
            </div>
          )}

          {assignments.length > 0 && (
            <>
              <div style={styles.assignedInfo}>
                Your group has been assigned {assignments.length} question{assignments.length !== 1 ? 's' : ''}.
                {quota === 2 ? ' You must complete 2.' : ' You must complete 1.'}
              </div>

              <div style={styles.assignedQuestions}>
                <div style={styles.assignedQuestionsTitle}>
                  Assigned Questions
                </div>

                {assignments.map(
                  (assignment, index) => (
                    <div
                      key={assignment.id}
                      style={styles.assignedQuestion}
                    >
                      <div
                        style={
                          styles.assignedQuestionNumber
                        }
                      >
                        Question {index + 1}
                      </div>

                      <div
                        style={
                          styles.assignedQuestionText
                        }
                      >
                        {
                          assignment
                            .cq_submissions
                            ?.question_text ||
                          'Question text unavailable.'
                        }
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* Question 1 selector */}
              <div style={styles.section}>
                <label style={styles.sectionLabel}>Question 1</label>
                <select
                  style={styles.select}
                  value={selectedQ1}
                  onChange={e => setSelectedQ1(e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value="">Select a question...</option>
                  {assignments.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.cq_submissions?.question_text?.slice(0, 80)}...
                    </option>
                  ))}
                </select>

                {selectedQ1 && (
                  <QuestionForm
                    assignment={assignments.find(a => a.id === selectedQ1)}
                    existingSubmission={mySubmissions.find(s => s.cq_question_assignment_id === selectedQ1)}
                    enrolmentId={myEnrolment.id}
                    onSaved={handleDraftSaved}
                    disabled={isReadOnly}
                  />
                )}
              </div>

              {/* Question 2 selector (only if quota is 2) */}
              {quota === 2 && (
                <div style={styles.section}>
                  <label style={styles.sectionLabel}>Question 2</label>
                  <select
                    style={styles.select}
                    value={selectedQ2}
                    onChange={e => setSelectedQ2(e.target.value)}
                    disabled={isReadOnly}
                  >
                    <option value="">Select a question...</option>
                    {availableForQ2.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.cq_submissions?.question_text?.slice(0, 80)}...
                      </option>
                    ))}
                  </select>

                  {selectedQ2 && (
                    <QuestionForm
                      assignment={assignments.find(a => a.id === selectedQ2)}
                      existingSubmission={mySubmissions.find(s => s.cq_question_assignment_id === selectedQ2)}
                      enrolmentId={myEnrolment.id}
                      onSaved={handleDraftSaved}
                      disabled={isReadOnly}
                    />
                  )}
                </div>
              )}

              {/* Other participating group members */}
              {!isReadOnly && (
                <div style={styles.section}>
                  <label style={styles.sectionLabel}>Other group members present (optional)</label>
                  <p style={styles.memberHint}>
                    If you are working alone, leave this blank. Otherwise, enter the student IDs of the other group members who are participating. Your own ID is included automatically.
                  </p>
                  <div style={styles.memberRow}>
                    <input
                      style={styles.memberInput}
                      type="text"
                      value={memberCodes}
                      onChange={e => {
                        setMemberCodes(e.target.value);
                        setResolvedMembers(null);
                        setMemberError('');
                      }}
                      placeholder="B11234, B11235..."
                    />
                    <button
                      style={styles.validateBtn}
                      onClick={handleResolveMember}
                      disabled={!memberCodes.trim()}
                    >
                      Validate
                    </button>
                  </div>
                  {memberError && <div style={styles.memberError}>{memberError}</div>}
                  {resolvedMembers && resolvedMembers.length > 0 && (
                    <div style={styles.memberConfirm}>
                      ✓ {resolvedMembers.map(m => m.code).join(', ')} validated
                    </div>
                  )}
                  {!memberCodes.trim() && !memberError && (
                    <div style={styles.memberSolo}>
                      Working alone is fine — only your own ID will be recorded.
                    </div>
                  )}
                </div>
              )}

              {/* Submit button */}
              {!isReadOnly && isOpen && (
                <button
                  style={styles.submitBtn}
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? 'Submitting...' : 'Submit Question Improvement'}
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  wrap: { padding: 0, maxWidth: 'none', margin: 0 },
  heading: { margin: '0 0 8px', fontSize: '26px', color: '#243b53' },
  muted: { color: '#627d98', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' },
  error: { background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '5px', padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px' },
  success: { background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '5px', padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px' },
  successBox: { background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '6px', padding: '16px', color: '#216e45', fontSize: '14px', marginBottom: '16px', fontWeight: '600' },
  notice: { background: '#f7f8fa', border: '1px solid #d9e2ec', borderRadius: '6px', padding: '16px', color: '#627d98', fontSize: '14px', marginBottom: '16px' },
  noticeWarning: { background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '6px', padding: '16px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px' },
  weekSelector: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' },
  weekBtn: { border: '1px solid #d9e2ec', background: 'white', borderRadius: '5px', padding: '8px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#243b53' },
  weekBtnActive: { background: '#2f6f9f', borderColor: '#2f6f9f', color: 'white' },
  weekBtnClosed: { color: '#627d98' },
  weekBtnFuture: { color: '#b0bec5' },
  assignedInfo: { background: '#eef5fa', border: '1px solid #c5d6e3', borderRadius: '5px', padding: '10px 14px', fontSize: '14px', color: '#2f6f9f', marginBottom: '16px' },

  assignedQuestions: {
    marginBottom: '22px'
  },

  assignedQuestionsTitle: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#243b53',
    marginBottom: '9px'
  },

  assignedQuestion: {
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    background: '#f7f8fa',
    padding: '12px 14px',
    marginBottom: '8px'
  },

  assignedQuestionNumber: {
    fontSize: '11px',
    fontWeight: '800',
    color: '#627d98',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '5px'
  },

  assignedQuestionText: {
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#243b53',
    whiteSpace: 'pre-wrap'
  },

  section: { marginBottom: '20px' },
  sectionLabel: { display: 'block', fontWeight: '700', fontSize: '14px', color: '#243b53', marginBottom: '8px' },
  select: { width: '100%', border: '1px solid #d9e2ec', borderRadius: '5px', padding: '9px 11px', fontSize: '14px', background: 'white', marginBottom: '12px', boxSizing: 'border-box' },
  memberHint: { fontSize: '13px', color: '#627d98', marginBottom: '8px' },
  memberRow: { display: 'flex', gap: '8px', marginBottom: '6px' },
  memberInput: { flex: 1, border: '1px solid #d9e2ec', borderRadius: '5px', padding: '9px 11px', fontSize: '14px', background: 'white' },
  validateBtn: { background: '#2f6f9f', color: 'white', border: 'none', borderRadius: '5px', padding: '9px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  memberError: { color: '#a12a2a', fontSize: '13px' },
  memberConfirm: { color: '#216e45', fontSize: '13px', fontWeight: '600' },
  memberSolo: { color: '#627d98', fontSize: '13px' },
  submitBtn: { background: '#2f6f9f', color: 'white', border: 'none', borderRadius: '5px', padding: '12px 24px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', width: '100%' },
};